// @ts-nocheck
import { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { motion, useAnimationFrame, useMotionValue } from "framer-motion";
import { TreeDeciduous, LogIn, Newspaper, ArrowLeft, Calendar, Image, Building2, Sparkles, ChevronDown } from "lucide-react";
import uaeFlag from "@/assets/uae-flag.png";
import uaeEmblem from "@/assets/uae-emblem-transparent.png";
import defaultBusiness from "@/assets/default-business.png";
import { useAuth } from "@/lib/auth";
import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { Card, CardContent } from "@/components/ui/card";

const NewsTicker = () => {
  const { data: news } = useQuery({
    queryKey: ["family-news-ticker"],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("family_news")
        .select("id, title, created_at")
        .order("created_at", { ascending: false })
        .limit(10);
      if (error) throw error;
      return data;
    },
  });

  if (!news?.length) return null;

  const tickerContent = news.map(n => n.title).join("   ✦   ");

  return (
    <div className="fixed top-[72px] left-0 right-0 z-40 bg-gradient-to-r from-primary/95 via-primary to-primary/95 backdrop-blur-sm overflow-hidden">
      <div className="flex items-center h-10">
        <div className="flex-shrink-0 bg-primary-foreground/10 px-5 h-full flex items-center gap-2 z-10 border-l border-primary-foreground/20">
          <Newspaper className="h-4 w-4 text-primary-foreground" />
          <span className="text-primary-foreground text-sm font-bold whitespace-nowrap font-tajawal">آخر الأخبار</span>
        </div>
        <div className="overflow-hidden flex-1">
          <div className="animate-scroll-rtl whitespace-nowrap text-primary-foreground/90 text-sm py-2 font-tajawal">
            {tickerContent}   ✦   {tickerContent}   ✦   {tickerContent}
          </div>
        </div>
      </div>
    </div>
  );
};

const HeroSection = () => {
  const { user } = useAuth();

  return (
    <section className="min-h-screen flex flex-col items-center justify-center relative pt-32 pb-20 px-4">
      {/* Background Effects */}
      <div className="absolute inset-0 overflow-hidden">
        <div className="absolute top-1/4 right-1/4 w-[600px] h-[600px] rounded-full bg-primary/8 blur-[150px] animate-pulse" />
        <div className="absolute bottom-1/4 left-1/4 w-[500px] h-[500px] rounded-full bg-accent/5 blur-[120px]" />
        <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[800px] h-[800px] rounded-full border border-primary/5" />
        <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[600px] h-[600px] rounded-full border border-primary/5" />
      </div>

      <motion.div
        initial={{ opacity: 0, y: 40 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 1, ease: "easeOut" }}
        className="text-center relative z-10 max-w-3xl"
      >
        {/* UAE Symbols */}
        <motion.div
          initial={{ opacity: 0, scale: 0.8 }}
          animate={{ opacity: 1, scale: 1 }}
          transition={{ delay: 0.2, duration: 0.8 }}
          className="mb-8 flex items-center justify-center gap-6"
        >
          <motion.img 
            src={uaeEmblem} 
            alt="شعار الإمارات" 
            className="h-32 md:h-40 object-contain drop-shadow-2xl"
            whileHover={{ scale: 1.05, rotate: 2 }}
            transition={{ type: "spring", stiffness: 300 }}
          />
          <motion.img 
            src={uaeFlag} 
            alt="علم الإمارات" 
            className="h-20 md:h-24 rounded-lg shadow-2xl shadow-black/30 object-contain"
            whileHover={{ scale: 1.05 }}
          />
        </motion.div>

        {/* Decorative Line */}
        <motion.div
          initial={{ scaleX: 0 }}
          animate={{ scaleX: 1 }}
          transition={{ delay: 0.4, duration: 0.8 }}
          className="mb-8 flex items-center justify-center gap-4"
        >
          <div className="w-16 h-px bg-gradient-to-r from-transparent to-primary/50" />
          <Sparkles className="w-5 h-5 text-primary/60" />
          <div className="w-16 h-px bg-gradient-to-l from-transparent to-primary/50" />
        </motion.div>

        {/* Main Title */}
        <motion.h1
          initial={{ opacity: 0, y: 30 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.3 }}
          className="font-amiri text-7xl md:text-9xl font-bold mb-6 leading-none"
        >
          <span className="gold-text">عائلة أبوليلة</span>
        </motion.h1>

        {/* Subtitle */}
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 0.5 }}
          className="mb-4"
        >
          <span className="inline-flex items-center gap-3 text-xl md:text-2xl text-primary/70 font-amiri tracking-wider">
            <span className="w-8 h-px bg-primary/40" />
            حفظ التراث والأنساب
            <span className="w-8 h-px bg-primary/40" />
          </span>
        </motion.div>

        {/* Description */}
        <motion.p
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 0.6 }}
          className="text-muted-foreground text-lg md:text-xl mb-12 leading-relaxed max-w-xl mx-auto font-tajawal"
        >
          مرحباً بكم في موقع عائلة أبوليلة، حيث نحفظ تاريخنا وأنسابنا
          ونوثّق روابطنا العائلية للأجيال القادمة
        </motion.p>

        {/* CTA Button */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.7 }}
          className="flex flex-col items-center gap-4"
        >
          {user ? (
            <Link to="/tree" data-testid="hero-tree-btn">
              <motion.div whileHover={{ scale: 1.02 }} whileTap={{ scale: 0.98 }}>
                <Button 
                  size="lg" 
                  className="relative overflow-hidden text-xl px-10 py-7 bg-gradient-to-r from-primary via-primary to-primary/90 text-primary-foreground rounded-2xl shadow-2xl shadow-primary/30 hover:shadow-primary/50 transition-all duration-500 group"
                >
                  <span className="absolute inset-0 bg-gradient-to-r from-transparent via-white/10 to-transparent -translate-x-full group-hover:translate-x-full transition-transform duration-1000" />
                  <TreeDeciduous className="h-6 w-6 ml-3" />
                  شجرة العائلة
                </Button>
              </motion.div>
              <p className="text-sm text-muted-foreground mt-3 font-tajawal">عرض شجرة العائلة بالتفاصيل الكاملة</p>
            </Link>
          ) : (
            <Link to="/login" data-testid="hero-login-btn">
              <motion.div whileHover={{ scale: 1.02 }} whileTap={{ scale: 0.98 }}>
                <Button 
                  size="lg" 
                  className="relative overflow-hidden text-xl px-10 py-7 bg-gradient-to-r from-primary via-primary to-primary/90 text-primary-foreground rounded-2xl shadow-2xl shadow-primary/30 hover:shadow-primary/50 transition-all duration-500 group"
                >
                  <span className="absolute inset-0 bg-gradient-to-r from-transparent via-white/10 to-transparent -translate-x-full group-hover:translate-x-full transition-transform duration-1000" />
                  <LogIn className="h-6 w-6 ml-3" />
                  دخول أفراد العائلة
                </Button>
              </motion.div>
              <p className="text-sm text-muted-foreground mt-3 font-tajawal">سجّل دخولك لعرض شجرة العائلة والتفاصيل</p>
            </Link>
          )}
        </motion.div>

        {/* Scroll Indicator */}
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 1.2 }}
          className="absolute bottom-8 left-1/2 -translate-x-1/2"
        >
          <motion.div
            animate={{ y: [0, 8, 0] }}
            transition={{ duration: 2, repeat: Infinity }}
            className="flex flex-col items-center gap-2 text-muted-foreground/50"
          >
            <span className="text-xs font-tajawal">اكتشف المزيد</span>
            <ChevronDown className="w-5 h-5" />
          </motion.div>
        </motion.div>
      </motion.div>
    </section>
  );
};

const SectionTitle = ({ icon: Icon, title, link, linkText }: { icon: any; title: string; link: string; linkText: string }) => (
  <div className="flex items-center justify-between mb-8">
    <div className="flex items-center gap-3">
      <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-primary/20 to-primary/5 flex items-center justify-center">
        <Icon className="h-6 w-6 text-primary" />
      </div>
      <h2 className="font-amiri text-3xl md:text-4xl font-bold gold-text">{title}</h2>
    </div>
    <Button asChild variant="ghost" className="text-primary hover:text-primary/80 hover:bg-primary/5 rounded-xl">
      <Link to={link} className="flex items-center gap-2 font-tajawal">
        {linkText}
        <ArrowLeft className="h-4 w-4" />
      </Link>
    </Button>
  </div>
);

const NewsCards = () => {
  const { data: news } = useQuery({
    queryKey: ["family-news-cards"],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("family_news")
        .select("id, title, image_url, news_date, created_at")
        .order("created_at", { ascending: false })
        .limit(6);
      if (error) throw error;
      return data;
    },
  });

  if (!news?.length) return null;

  return (
    <section className="w-full max-w-6xl mx-auto px-4 py-16">
      <SectionTitle icon={Newspaper} title="آخر الأخبار" link="/news" linkText="عرض الكل" />
      
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
        {news.map((item, i) => (
          <motion.div
            key={item.id}
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ delay: i * 0.1 }}
          >
            <Link to="/news" data-testid={`news-card-${item.id}`}>
              <Card className="glass-card glass-card-hover overflow-hidden group cursor-pointer h-full transition-all duration-500">
                <div className="relative w-full h-48 overflow-hidden bg-gradient-to-br from-secondary/50 to-secondary/20">
                  {item.image_url ? (
                    <img 
                      src={item.image_url} 
                      alt={item.title} 
                      className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-700" 
                      loading="lazy" 
                    />
                  ) : (
                    <div className="w-full h-full flex items-center justify-center">
                      <Newspaper className="h-16 w-16 text-primary/10" />
                    </div>
                  )}
                  <div className="absolute inset-0 bg-gradient-to-t from-background/90 via-transparent to-transparent" />
                </div>
                <CardContent className="p-5 relative">
                  <div className="flex items-center gap-2 text-muted-foreground text-sm mb-3">
                    <Calendar className="h-4 w-4 text-primary/60" />
                    <span className="font-tajawal">{new Date(item.news_date || item.created_at).toLocaleDateString("ar-SA")}</span>
                  </div>
                  <h3 className="font-amiri text-lg font-bold text-foreground group-hover:text-primary transition-colors line-clamp-2">
                    {item.title}
                  </h3>
                </CardContent>
              </Card>
            </Link>
          </motion.div>
        ))}
      </div>
    </section>
  );
};

const LatestGallery = () => {
  const { data: photos } = useQuery({
    queryKey: ["gallery-latest"],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("gallery_photos")
        .select("id, photo_url, title")
        .order("created_at", { ascending: false })
        .limit(8);
      if (error) throw error;
      return data;
    },
  });

  if (!photos?.length) return null;

  return (
    <section className="w-full max-w-6xl mx-auto px-4 py-16">
      <SectionTitle icon={Image} title="أحدث الصور" link="/gallery" linkText="عرض المعرض" />
      
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        {photos.map((photo, i) => (
          <motion.div
            key={photo.id}
            initial={{ opacity: 0, scale: 0.9 }}
            whileInView={{ opacity: 1, scale: 1 }}
            viewport={{ once: true }}
            transition={{ delay: i * 0.05 }}
          >
            <Link to="/gallery" data-testid={`gallery-photo-${photo.id}`}>
              <div className="glass-card glass-card-hover rounded-2xl overflow-hidden group cursor-pointer aspect-square">
                <div className="relative w-full h-full bg-gradient-to-br from-secondary/30 to-secondary/10">
                  <img 
                    src={photo.photo_url} 
                    alt={photo.title || "صورة"} 
                    className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-700" 
                    loading="lazy" 
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-background/80 via-transparent to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300 flex items-end p-4">
                    {photo.title && (
                      <p className="text-sm text-foreground font-tajawal truncate">{photo.title}</p>
                    )}
                  </div>
                </div>
              </div>
            </Link>
          </motion.div>
        ))}
      </div>
    </section>
  );
};

const FamilyBusinesses = () => {
  const { data: businesses } = useQuery({
    queryKey: ["family-businesses-home"],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("family_businesses")
        .select("*")
        .order("created_at", { ascending: false });
      if (error) throw error;
      return data;
    },
  });

  const trackRef = useRef<HTMLDivElement | null>(null);
  const x = useMotionValue(0);
  const [loopWidth, setLoopWidth] = useState(0);

  useEffect(() => {
    const updateWidth = () => {
      if (!trackRef.current) return;
      setLoopWidth(trackRef.current.scrollWidth);
    };
    updateWidth();
    window.addEventListener("resize", updateWidth);
    return () => window.removeEventListener("resize", updateWidth);
  }, [businesses]);

  useAnimationFrame((_, delta) => {
    if (!loopWidth) return;
    const speed = 0.03;
    const nextX = x.get() - delta * speed;
    x.set(nextX <= -loopWidth ? 0 : nextX);
  });

  if (!businesses?.length) return null;

  const BusinessCard = ({ biz, isClone = false }: { biz: any; isClone?: boolean }) => (
    <Link 
      to="/businesses" 
      key={isClone ? `${biz.id}-clone` : biz.id} 
      className="flex flex-col items-center gap-4 w-40 flex-shrink-0 group cursor-pointer"
      data-testid={`business-card-${biz.id}`}
    >
      <div className="relative">
        <div className="absolute -inset-1 bg-gradient-to-r from-primary/30 to-primary/10 rounded-full blur opacity-0 group-hover:opacity-100 transition-opacity duration-500" />
        <div className="relative w-28 h-28 rounded-full overflow-hidden bg-gradient-to-br from-secondary/50 to-secondary/20 flex items-center justify-center border-2 border-primary/20 group-hover:border-primary/50 transition-all duration-300 shadow-xl">
          <img
            src={biz.logo_url || defaultBusiness}
            alt={biz.name_ar}
            className={`w-full h-full ${biz.logo_url ? 'object-cover' : 'object-contain p-3'}`}
            loading="lazy"
          />
        </div>
      </div>
      <div className="text-center">
        <h3 className="font-amiri text-base font-bold text-foreground group-hover:text-primary transition-colors line-clamp-2">{biz.name_ar}</h3>
        {biz.name_en && <p className="text-xs text-muted-foreground font-tajawal mt-1">{biz.name_en}</p>}
      </div>
    </Link>
  );

  return (
    <section className="w-full max-w-6xl mx-auto px-4 py-16">
      <SectionTitle icon={Building2} title="شركات العائلة" link="/businesses" linkText="عرض كافة الشركات" />
      
      <div className="overflow-hidden [mask-image:linear-gradient(to_left,transparent,black_5%,black_95%,transparent)] py-4">
        <motion.div className="flex w-max gap-8" style={{ x }}>
          <div ref={trackRef} className="flex gap-8 shrink-0">
            {businesses.map((biz) => <BusinessCard key={biz.id} biz={biz} />)}
          </div>
          <div aria-hidden="true" className="flex gap-8 shrink-0">
            {businesses.map((biz) => <BusinessCard key={`${biz.id}-clone`} biz={biz} isClone />)}
          </div>
        </motion.div>
      </div>
    </section>
  );
};

const Index = () => {
  return (
    <div className="min-h-screen relative">
      <NewsTicker />
      <HeroSection />
      <NewsCards />
      <LatestGallery />
      <FamilyBusinesses />
    </div>
  );
};

export default Index;
